import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DragulaService } from 'ng2-dragula';
import { Subscription } from 'rxjs';
import { Batch } from 'src/app/_models/batch-number';
import { Operation } from 'src/app/_models/operation';
import { Product } from 'src/app/_models/product';
import { ProductType } from 'src/app/_models/product_type';
import { MessService } from 'src/app/_services/mess-service.service';
import { OperationsService } from 'src/app/_services/operations.service';
import { ProductTypesService } from 'src/app/_services/product-types.service';
import { ProductsService } from 'src/app/_services/products.service';

@Component({
  selector: 'app-products-copy',
  templateUrl: './products-copy.component.html',
})
export class ProductsCopyComponent implements OnInit, OnDestroy {
  copyProductForm: FormGroup;

  productTypes: ProductType[];

  allOperations: Operation[];

  operations: FormArray;
  suboperations: FormArray;

  batchNumber: number;

  productForCopy: Product;

  subscriptions: Subscription[] = [];

  constructor(
    private dragulaService: DragulaService,
    private messService: MessService,
    private productTypesService: ProductTypesService,
    private productsService: ProductsService,
    private operationsService: OperationsService,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute
  ) {
    dragulaService.createGroup('OPERATIONS', {
      moves: (el, container, handle) => {
        return handle.className === 'handle';
      },
    });
  }

  // * Lifecycles === //
  ngOnInit(): void {
    // * Fetch all data for form fields
    this.subscriptions.push(
      this.operationsService.getAllOperations().subscribe((operations) => {
        this.allOperations = operations;
        if (!operations) {
          this.router.navigate(['./../'], { relativeTo: this.route });
          return;
        }
        this.populateForm();
      })
    );

    this.subscriptions.push(
      this.productTypesService
        .getAllProductTypes()
        .subscribe((productTypes) => {
          this.productTypes = productTypes;
        })
    );

    this.copyProductForm = this.fb.group({
      batch: [{ value: '', disabled: true }, Validators.required],
      label: ['', Validators.required],
      name: ['', Validators.required],
      product_type_id: ['', Validators.required],
      description: [''],
      total_estimated_time: [{ value: '', disabled: true }],
      operations: this.fb.array([]),
    });
    this.operations = this.copyProductForm.get('operations') as FormArray;
  }

  ngOnDestroy() {
    this.dragulaService.destroy('OPERATIONS');
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.messService.clearErrors();
  }

  // * Helpers === //
  updateTotalEstimatedTime() {
    let totalEstTime = 0;
    this.getOperationsControls.value.forEach((operation: Operation) => {
      totalEstTime +=
        +operation.prep_time +
        +operation.operation_time +
        +operation.next_op_time;
    });
    this.copyProductForm.patchValue({
      total_estimated_time: totalEstTime,
    });
  }

  private populateForm() {
    this.subscriptions.push(
      this.productsService.getBatchNumber().subscribe((batchNumber: Batch) => {
        this.batchNumber = batchNumber.batch;
        this.copyProductForm.patchValue({
          batch: `${this.batchNumber}`,
        });
      })
    );

    this.subscriptions.push(
      this.productsService.copyProductData.subscribe((productData: Product) => {
        this.productForCopy = productData;
        this.copyProductForm.patchValue({
          label: `${this.productForCopy.label}`,
          name: this.productForCopy.name,
          product_type_id: this.productForCopy.product_type_id,
          description: this.productForCopy.description,
        });
      })
    );

    this.productForCopy.operations.forEach((operation: Operation, index) => {
      this.operations.push(
        this.fb.group({
          operation_id: [operation.operation_id, Validators.required],
          prep_time: [operation.prep_time],
          operation_time: [operation.operation_time],
          next_op_time: [operation.next_op_time],
          suboperations: this.fb.array([]),
        })
      );

      // If suboperation exist
      if (operation.suboperations.length) {
        const subOperation = this.getOperationsControls.controls[index].get(
          'suboperations'
        ) as FormArray;
        operation.suboperations.forEach((suboperation) => {
          subOperation.push(
            this.fb.group({
              suboperation_id: [
                suboperation.suboperation_id,
                Validators.required,
              ],
              prep_time: [suboperation.prep_time],
              operation_time: [suboperation.operation_time],
              next_op_time: [suboperation.next_op_time],
            })
          );
        });
      }
    });
    this.updateTotalEstimatedTime();
  }

  get getOperationsControls(): FormArray {
    return this.copyProductForm.get('operations') as FormArray;
  }

  getOperationsControlsByIndex(index: number): FormGroup {
    return this.getOperationsControls.controls[index] as FormGroup;
  }

  getSuboperationsControls(index: number): any {
    const operation = this.getOperationsControls.controls[index] as FormGroup;
    return operation.get('suboperations') as FormArray;
  }

  updateOperationFields(operation, field: string[]) {
    field.forEach((fieldName) => {
      const object = {};
      const newValueSum = operation.controls.suboperations.value
        .map((val) => parseInt(val[fieldName]))
        .reduce((acc, current) => {
          return acc + current;
        });

      object[fieldName] = newValueSum;

      operation.patchValue(object);
    });
    this.updateTotalEstimatedTime();
  }

  // * Add/Create/Remove/Update operations & suboperations from form === //
  createOperation(): FormGroup {
    return this.fb.group({
      operation_id: ['', Validators.required],
      prep_time: [''],
      operation_time: [''],
      next_op_time: [''],
      suboperations: this.fb.array([]),
    });
  }

  createSuboperation(): FormGroup {
    return this.fb.group({
      suboperation_id: ['', Validators.required],
      prep_time: [''],
      operation_time: [''],
      next_op_time: [''],
    });
  }

  addOperation(): void {
    this.operations.push(this.createOperation());
    this.updateTotalEstimatedTime();
  }

  removeOperation(operationId: number) {
    this.operations.removeAt(operationId);
    this.updateTotalEstimatedTime();
  }

  removeSuboperation(suboperationIndex: number, operationIndex: number) {
    const subOperation = this.getOperationsControls.controls[
      operationIndex
    ].get('suboperations') as FormArray;

    const parentOperation = this.getOperationsControls.controls[operationIndex];
    let parentPrepTime = +parentOperation.get('prep_time').value;
    let parentOperationTime = +parentOperation.get('operation_time').value;
    let parentNextOpTimeSum = +parentOperation.get('next_op_time').value;
    parentOperation.patchValue({
      prep_time:
        parentPrepTime -
        subOperation.controls[suboperationIndex].get('prep_time').value,
      operation_time:
        parentOperationTime -
        subOperation.controls[suboperationIndex].get('operation_time').value,
      next_op_time:
        parentNextOpTimeSum -
        subOperation.controls[suboperationIndex].get('next_op_time').value,
    });

    subOperation.removeAt(suboperationIndex);
    this.updateTotalEstimatedTime();
  }

  addSuboperation(index: number): void {
    const subOperation = this.getOperationsControls.controls[index].get(
      'suboperations'
    ) as FormArray;
    subOperation.push(this.createSuboperation());
    this.updateTotalEstimatedTime();
  }

  onChangeOperation(event, formGroupIndex) {
    const selectedOperationId = event.value;
    const subOperation = this.getOperationsControls.controls[
      formGroupIndex
    ].get('suboperations') as FormArray;
    subOperation.clear(); // Clear all subOperations if you change operation

    this.allOperations.filter((operation) => {
      if (operation.id === selectedOperationId) {
        const fGroup = this.operations.at(formGroupIndex);
        fGroup.patchValue({
          prep_time: `${operation.prep_time}`,
          operation_time: `${operation.operation_time}`,
          next_op_time: `${operation.next_op_time}`,
        });
      }
    });
    this.updateTotalEstimatedTime();
  }

  onChangeSuboperation(
    event,
    operationId,
    formGroupIndex,
    subOpIndex,
    operation: FormGroup,
    fields: string[]
  ) {
    const selectedSuboperationId = event.value;
    const allSubOps = this.findOperation(operationId);

    allSubOps.filter((suboperation) => {
      if (suboperation.id === selectedSuboperationId) {
        const fGroup =
          this.getSuboperationsControls(formGroupIndex).controls[subOpIndex];
        fGroup.patchValue({
          prep_time: `${suboperation.prep_time}`,
          operation_time: `${suboperation.operation_time}`,
          next_op_time: `${suboperation.next_op_time}`,
        });
      }
    });

    this.updateOperationFields(operation, fields);
    this.updateTotalEstimatedTime();
  }

  findOperation(operationId) {
    let subOp;
    this.allOperations.filter((operation) => {
      if (operation.id === operationId) {
        subOp = operation.suboperations;
      }
    });
    return subOp;
  }

  // * Copy product === //
  onCopyProduct() {
    const product = this.copyProductForm.getRawValue();
    this.subscriptions.push(
      this.productsService.addProduct(product).subscribe(
        () => {
          this.messService.add({
            severity: 'success',
            title: 'Uspešno kreiranje',
            detail: 'Kreirali ste novi proizvod.',
          });
          this.router.navigate(['./../'], { relativeTo: this.route });
        },
        (error) => {
          const errorObj = error.error.errors;
          const indexOfInvalidField = error.error.item - 1;
          let form;

          if (indexOfInvalidField < 0) {
            form = this.copyProductForm;
          } else {
            form = this.getOperationsControls.controls[indexOfInvalidField];
          }
          const messService = this.messService;
          Object.keys(errorObj).forEach(function (key) {
            const formControl = form.get(key);
            if (formControl) {
              // If error exist set red border to field
              formControl.setErrors({});
            }
            // Push toast error for every field
            messService.addError({
              severity: 'error',
              title: 'Greška',
              detail: errorObj[key],
              sticky: true,
            });
          });
        }
      )
    );
  }
}
